/**
 * Vault unlock — the portal side.
 *
 * The server is the truth (2026-10). A successful unlock — POST
 * /api/account/vault-security/verify (PIN), a vault passkey, or setting /
 * resetting the PIN — writes the HttpOnly `pm_vault` cookie, and the
 * medical-vault endpoints answer 403 `{ error: "vault_locked" }` without it.
 * GET /api/account/vault-security reports whether this browser holds a live
 * unlock (`unlocked`, `unlockExpiresInSeconds`).
 *
 * What is kept here, in sessionStorage, is only a UX hint: until when this tab
 * believes it is unlocked, aligned with the account's unlock duration — so the
 * gate comes back (and POST /lock is sent) when the server's unlock ends, and
 * the page never shows a protected area the API would refuse. A protected
 * area is never opened on the hint alone when the server answered.
 *
 * Window events:
 *   - "vault-unlocked" — an unlock was recorded in this tab;
 *   - "vault-locked"   — the unlock ended (timer, the Lock action, or the API
 *     answered vault_locked); every ProtectedContent shows its gate again.
 *     "vault-protection-changed" goes out with it for the listeners that
 *     predate it (ProtectionLockIcon re-reads isVaultUnlocked() on it).
 */

import { ApiError, apiFetch } from "@pontimed/api-client";

const UNLOCK_UNTIL_KEY = "pontimed_vault_unlocked_until";
/** The pre-2026-10 hint (unlock time + minutes) — cleared, never read. */
const LEGACY_KEYS = ["pontimed_vault_unlocked", "pontimed_vault_ttl"];
const DEFAULT_TTL_MINUTES = 5;

function readUntil(): number | null {
  try {
    const raw = sessionStorage.getItem(UNLOCK_UNTIL_KEY);
    const until = raw ? Number(raw) : Number.NaN;
    return Number.isFinite(until) ? until : null;
  } catch {
    return null;
  }
}

/** When this tab's unlock hint ends (ms since epoch), or null when it holds none. */
export function getVaultUnlockExpiry(): number | null {
  if (typeof window === "undefined") return null;
  const until = readUntil();
  return until !== null && until > Date.now() ? until : null;
}

export function isVaultUnlocked(): boolean {
  return getVaultUnlockExpiry() !== null;
}

/**
 * Record an unlock the server has just granted, and return when it ends (ms
 * since epoch). `expiresInSeconds` — what the server reports left on its
 * cookie — wins over the account's `unlockMinutes`.
 */
export function markVaultUnlocked(
  unlockMinutes?: number,
  expiresInSeconds?: number | null,
): number {
  const ms =
    typeof expiresInSeconds === "number"
      ? expiresInSeconds * 1000
      : (unlockMinutes || DEFAULT_TTL_MINUTES) * 60 * 1000;
  const until = Date.now() + ms;
  if (typeof window === "undefined") return until;
  try {
    sessionStorage.setItem(UNLOCK_UNTIL_KEY, String(until));
  } catch {
    // Storage blocked: each gate still keeps its own copy of `until`.
  }
  window.dispatchEvent(new Event("vault-unlocked"));
  return until;
}

/** Forget this tab's unlock hint (the server's cookie is untouched). */
export function clearVaultUnlockHint(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(UNLOCK_UNTIL_KEY);
    for (const key of LEGACY_KEYS) sessionStorage.removeItem(key);
  } catch {
    // Nothing stored, nothing to clear.
  }
}

function announceLocked(): void {
  window.dispatchEvent(new Event("vault-locked"));
  window.dispatchEvent(new Event("vault-protection-changed"));
}

/**
 * End the unlock — in this tab and on the server (POST /lock clears
 * `pm_vault`). Never throws: the cookie ends with its own Max-Age anyway.
 */
export async function lockVault(): Promise<void> {
  if (typeof window === "undefined") return;
  clearVaultUnlockHint();
  announceLocked();
  try {
    await apiFetch.post("/api/account/vault-security/lock");
  } catch {
    // Offline or signed out — nothing more to do.
  }
}

/** Is `err` the API's 403 `vault_locked` (an apiFetch get/post/put/patch/delete)? */
export function isVaultLockedError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const body = err.body as { error?: string } | null;
  return body?.error === "vault_locked";
}

/** Is `res` (an apiFetch.raw answer) the API's 403 `vault_locked`? Leaves the body unread. */
export async function isVaultLockedResponse(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.clone().json()) as { error?: string } | null;
    return body?.error === "vault_locked";
  } catch {
    return false;
  }
}

/** The API refused for want of an unlock: drop the hint and bring the gates back. */
export function reportVaultLocked(): void {
  if (typeof window === "undefined") return;
  clearVaultUnlockHint();
  announceLocked();
}

/**
 * For a catch block around a medical-vault call: true — and the PIN gate is
 * shown again — when `err` is the API's `vault_locked`; the caller then shows
 * no error of its own. False for any other failure.
 */
export function handleVaultLocked(err: unknown): boolean {
  if (!isVaultLockedError(err)) return false;
  reportVaultLocked();
  return true;
}
